import { computed, type ComputedRef, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useEntitlementsStore } from '@/store/useEntitlementsStore';
import { useOrganizationStore } from '@/store/useOrganizationStore';
import { useLimitsModal, mapPlanCodeToTier } from '@/storybook/templates/limits';
import type { PlanTier } from '@/storybook/templates/limits/planFeatures';
import { useTrack, TrackEvents } from '@/composables/useTrack';
import { useFeatures } from '@/composables/useFeatures';

/**
 * Computes a human-readable reset date string (first of next month).
 * E.g. "Resets Aug 1st"
 */
const computeResetDate = (): string => {
  const now = new Date();
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const month = nextMonth.toLocaleString('en-US', { month: 'short' });
  return `Resets ${month} 1st`;
};

/**
 * Central composable that converts raw entitlements data into the format
 * the Storybook limits components expect.
 *
 * Limit computations (limitUsages, averagePercentage, hasLimits) live in
 * useEntitlementsStore as getters. This composable wires up the modal action,
 * routing, and tracking on top.
 */
export const useLimits = () => {
  const entitlementsStore = useEntitlementsStore();
  const organizationStore = useOrganizationStore();
  const { showLimitsModal } = useLimitsModal();
  const { track } = useTrack();
  const { getFeature } = useFeatures();

  const isPricingUpdateEnabled: Ref<boolean | undefined> = getFeature<boolean>('pricing-update-2025', false);

  const { isLoaded, isLoading, limitUsages, averagePercentage, hasLimits } = storeToRefs(entitlementsStore);

  const planTier: ComputedRef<PlanTier> = computed(() => {
    return mapPlanCodeToTier(organizationStore.planCode);
  });

  const criticalLimit = computed(() => {
    if (!hasLimits.value || !isLoaded.value) {
      return null;
    }

    const nonDisabledLimits = limitUsages.value.filter(limit => !limit.disabled);
    if (nonDisabledLimits.length === 0) {
      return null;
    }

    return nonDisabledLimits.reduce((worst, current) => {
      const worstPct = worst.max > 0 ? worst.current / worst.max : 0;
      const currentPct = current.max > 0 ? current.current / current.max : 0;
      return currentPct > worstPct ? current : worst;
    }, nonDisabledLimits[0]);
  });

  const showLimitAlert = computed(() => {
    if (!criticalLimit.value) {
      return false;
    }

    const usagePercentage = criticalLimit.value.max > 0 ? criticalLimit.value.current / criticalLimit.value.max : 0;
    return usagePercentage >= 0.8;
  });

  const limitAlertMessage = computed(() => {
    if (!criticalLimit.value) {
      return '';
    }

    const normalizedLabel = criticalLimit.value.label.toLowerCase();
    if (criticalLimit.value.current >= criticalLimit.value.max) {
      return `You've reached your limit of ${criticalLimit.value.max} ${normalizedLabel}.`;
    }

    return `You're close to reaching your limit of ${criticalLimit.value.max} ${normalizedLabel}.`;
  });

  const openLimitsModal = () => {
    track(TrackEvents.LIMITS_MODAL_VIEWED);

    const modalUsage = planTier.value === 'free' ? limitUsages.value : limitUsages.value.filter(l => !l.disabled);

    showLimitsModal({
      usage: modalUsage,
      resetDate: limitUsages.value.length ? computeResetDate() : '',
      plan: planTier.value,
      onExplorePlans: () => {
        window.location.href = '/plans?utm_source=web&utm_campaign=limit-modal';
      },
      onTalkToSam: () => {
        window.open('https://zight.com/contact-sales?utm_source=share.zight.com-upgrade-modal&utm_medium=web', '_blank', 'noopener');
      },
    });
  };

  return {
    limitUsages,
    averagePercentage,
    planTier,
    hasLimits,
    isLoaded,
    isLoading,
    isPricingUpdateEnabled,
    criticalLimit,
    showLimitAlert,
    limitAlertMessage,
    openLimitsModal,
  };
};
